import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils/utils';
import { buttonVariants } from '@/components/common/button';
function Pagination(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("mx-auto flex w-full justify-center", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <nav aria-label="pagination" role="navigation" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
const PaginationContent = React.forwardRef<HTMLOListElement, React.ComponentProps<'ol'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-row items-center gap-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <ol ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
PaginationContent.displayName = 'PaginationContent';
const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <li ref={ref} className={t1} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
PaginationItem.displayName = 'PaginationItem';
function PaginationLink(t0) {
  const $ = _c(13);
  let className;
  let isActive;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      isActive,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = isActive;
    $[3] = props;
    $[4] = t1;
  } else {
    className = $[1];
    isActive = $[2];
    props = $[3];
    t1 = $[4];
  }
  const size = t1 === undefined ? "icon" : t1;
  const t2 = isActive ? "page" : undefined;
  let t3;
  if ($[5] !== className || $[6] !== isActive || $[7] !== size) {
    t3 = cn(buttonVariants({
      variant: isActive ? "outline" : "ghost",
      size
    }), className);
    $[5] = className;
    $[6] = isActive;
    $[7] = size;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props || $[10] !== t2 || $[11] !== t3) {
    t4 = <Link aria-current={t2} className={t3} {...props} />;
    $[9] = props;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function PaginationPrevious(t0) {
  const $ = _c(12);
  let className;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      className,
      size,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = size;
  } else {
    className = $[1];
    props = $[2];
    size = $[3];
  }
  const t1 = size ?? "default";
  let t2;
  if ($[4] !== className) {
    t2 = cn("gap-1 pl-2.5", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronLeft className="h-4 w-4" />;
    t4 = <span>Previous</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[8] !== props || $[9] !== t1 || $[10] !== t2) {
    t5 = <PaginationLink aria-label="Go to previous page" size={t1} className={t2} {...props}>{t3}{t4}</PaginationLink>;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function PaginationNext(t0) {
  const $ = _c(12);
  let className;
  let props;
  let size;
  if ($[0] !== t0) {
    ({
      className,
      size,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = size;
  } else {
    className = $[1];
    props = $[2];
    size = $[3];
  }
  const t1 = size ?? "default";
  let t2;
  if ($[4] !== className) {
    t2 = cn("gap-1 pr-2.5", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span>Next</span>;
    t4 = <ChevronRight className="h-4 w-4" />;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[8] !== props || $[9] !== t1 || $[10] !== t2) {
    t5 = <PaginationLink aria-label="Go to next page" size={t1} className={t2} {...props}>{t3}{t4}</PaginationLink>;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function PaginationEllipsis(t0) {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex h-9 w-9 items-center justify-center", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MoreHorizontal className="h-4 w-4" />;
    t3 = <span className="sr-only">More pages</span>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== t1) {
    t4 = <span aria-hidden={true} className={t1} {...props}>{t2}{t3}</span>;
    $[7] = props;
    $[8] = t1;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious };